import type {
  WorkforceInterest,
  WorkforceInterestRecord,
  WorkforceInterestPage,
  DashboardReadout,
  OrganizationIntake,
  OrganizationQueuePage,
} from "@sourceenergy/sidekick-contracts";

const baseUrl = (
  import.meta.env.VITE_API_BASE_URL as string | undefined
)?.replace(/\/$/, "");

async function request<T>(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<T> {
  if (!baseUrl) throw new Error("SideKick API is not configured.");
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    cache: "no-store",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json",
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as
      | { error?: { message?: string } }
      | undefined;
    throw Object.assign(
      new Error(
        body?.error?.message ??
          `Request failed with status ${response.status}.`,
      ),
      { status: response.status },
    );
  }
  return response.json() as Promise<T>;
}

export const api = {
  recordWorkforceInterest: (
    token: string,
    intake: WorkforceInterest,
    key: string,
  ) =>
    request<WorkforceInterestRecord>("/v1/workforce/interests", token, {
      signal: AbortSignal.timeout(15000),
      method: "POST",
      headers: { "idempotency-key": key },
      body: JSON.stringify(intake),
    }),
  workforceInterests: (token: string, signal?: AbortSignal) =>
    request<WorkforceInterestPage>(
      "/v1/workforce/interests",
      token,
      signal ? { signal } : undefined,
    ),
  smsConversation: (token: string, phone: string) =>
    request<{ messages: SmsMessage[]; optedOut: boolean; truncated: boolean }>(
      "/v1/sms/conversation",
      token,
      { method: "POST", body: JSON.stringify({ phone }) },
    ),
  sendSms: (
    token: string,
    body: { to: string; message: string; consentReference: string },
    key: string,
  ) =>
    request<{ id: string; status: "ACCEPTED"; providerMessageId: string }>(
      "/v1/sms/messages",
      token,
      {
        method: "POST",
        headers: { "idempotency-key": key },
        body: JSON.stringify(body),
      },
    ),
  dashboard: (token: string, signal?: AbortSignal) =>
    request<DashboardReadout>(
      "/v1/dashboard",
      token,
      signal ? { signal } : undefined,
    ),
  organizations: async (
    token: string,
    filters: {
      q?: string;
      state?: string;
      category?: string;
      subcategory?: string;
      page: number;
      pageSize: number;
    },
    signal?: AbortSignal,
  ) => {
    const search = new URLSearchParams({
      page: String(filters.page),
      pageSize: String(filters.pageSize),
    });
    if (filters.q) search.set("q", filters.q);
    if (filters.state) search.set("state", filters.state);
    if (filters.category) search.set("category", filters.category);
    if (filters.subcategory) search.set("subcategory", filters.subcategory);
    const response = await request<OrganizationQueuePage | DashboardReadout>(
      `/v1/organizations?${search.toString()}`,
      token,
      signal ? { signal } : undefined,
    );
    if ("organizationQueue" in response) {
      return {
        items: Array.isArray(response.organizationQueue)
          ? response.organizationQueue
          : [],
        total: response.counts?.organizations ?? 0,
        page: filters.page,
        pageSize: filters.pageSize,
      } satisfies OrganizationQueuePage;
    }
    return response;
  },
  intakeOrganization: (token: string, intake: OrganizationIntake) =>
    request<{ id: string; state: string }>("/v1/organizations/intake", token, {
      method: "POST",
      body: JSON.stringify(intake),
    }),
};

export interface SmsMessage {
  id: string;
  direction: "INBOUND" | "OUTBOUND";
  message: string;
  receivedAt: string;
  status: "RECEIVED" | "PENDING" | "ACCEPTED";
}
